import { apiGet, apiPost } from './client';

// ============================================================================
// Matrix Benchmark — TypeScript mirrors of MatrixContracts.cs DTOs.
// ============================================================================

export interface MatrixRunRequest {
  datasetId: string;
  promptIds: string[];
  chatModelIds: string[];             // canonical "providerId:modelName" — drives fan-out
  evaluatorName?: string;
}

export interface MatrixPreflightRequest {
  datasetId: string;
  promptIds: string[];
  chatModelIds: string[];
}

export interface MatrixPreflightResponse {
  questionCount: number;
  promptCount: number;
  modelCount: number;
  cellCount: number;                  // Q × P × M
  estimatedCostUsd: number;
  estimatedSeconds: number;
  perModel: {
    chatModelId: string;
    displayLabel: string;
    estimatedCostUsd: number;
  }[];
}

export interface MatrixCellDto {
  questionId: string;
  questionOrderIndex: number;
  questionText: string;
  promptId: string;
  promptName: string;
  promptVersionNumber: number;
  providerId: string;
  providerName: string;
  modelName: string;
  chatModelId: string;                // M2+
  answer: string;
  citations: string[];
  expectedKeywords: string[];
  matchedKeywords: string[];
  inputTokens: number;
  outputTokens: number;
  estimatedCostUsd: number;
  latencyMs: number;
  groundednessScore: number;
  relevanceScore: number;
  citationScore: number;
  safetyScore: number;
  hallucinationRisk: number;
  overallScore: number;
  isError: boolean;
  error: string | null;
}

export interface MatrixAggregateDto {
  promptId: string;
  promptName: string;
  providerId: string;
  providerName: string;
  modelName: string;
  chatModelId: string;              // M2+: canonical "providerId:modelName"
  runCount: number;
  errorCount: number;
  avgOverallScore: number;
  avgGroundedness: number;
  avgRelevance: number;
  avgCitation: number;
  avgSafety: number;
  avgHallucinationRisk: number;
  totalCostUsd: number;
  avgLatencyMs: number;
}

export interface MatrixRunResponse {
  runId: string;
  datasetId: string;
  datasetName: string;
  role: string;
  promptIds: string[];
  providerIds: string[];
  chatModelIds: string[];             // M2+
  questionCount: number;
  promptCount: number;
  providerCount: number;
  chatModelCount: number;             // M2+
  cells: MatrixCellDto[];
  aggregates: MatrixAggregateDto[];
  /** Ranked "{promptId}|{providerId}" cell keys, best first. */
  rankedCells: string[];
  recommendation: string;
  timestamp: string;
}

export interface MatrixListItem {
  runId: string;
  datasetId: string;
  datasetName: string;
  role: string;
  promptCount: number;
  providerCount: number;
  chatModelCount: number;             // M2+
  questionCount: number;
  winningCellKey: string | null;
  winningScore: number;
  createdAt: string;
}

// ============================================================================
// API client functions
// ============================================================================

export const runMatrix = (body: MatrixRunRequest) =>
  apiPost<MatrixRunResponse>('/api/benchmarks/matrix', body);

export const listMatrixRuns = (limit = 20) =>
  apiGet<MatrixListItem[]>(`/api/benchmarks/matrix?limit=${limit}`);

export const getMatrixRun = (id: string) =>
  apiGet<MatrixRunResponse>(`/api/benchmarks/matrix/${id}`);

export const preflightMatrix = (body: MatrixPreflightRequest) =>
  apiPost<MatrixPreflightResponse>('/api/benchmarks/matrix/preflight', body);
